import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "../../../Contexts/AuthContext";
import { useTenant } from "../../../Contexts/TenantContext";
import { orderService } from "../../../Api/Services/orderService";
import { SimplifiedOrder } from "../../../Models/Order";
import { OrderSource } from "../../../Enums/OrderSource";
import { FaPlus, FaShoppingCart } from "react-icons/fa";
import { toast } from "react-toastify";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import PageTitle from "../../../Components/PageTitle/PageTitle";
import OrderCard from "../../../Components/OrderCard/OrderCard";
import Pagination from "../../../Components/Pagination/Pagination";
import PaginationSummary from "../../../Components/Pagination/PaginationSummary";
import Button from "../../../Components/Button/Button";

const AdminInStoreOrderPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { tenantPreference } = useTenant();
  const basePath = isAdmin ? "/admin" : "/operador";

  const [inStoreOrders, setInStoreOrders] = useState<SimplifiedOrder[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [listPage, setListPage] = useState(1);
  const [listTotalPages, setListTotalPages] = useState(1);
  const [listTotalItems, setListTotalItems] = useState(0);
  const ordersPerPage = 8;

  const loadInStoreOrders = useCallback(async (page: number = 1) => {
    setIsLoadingOrders(true);
    try {
      const data = await orderService.getAll({
        page,
        pageSize: ordersPerPage,
        orderSource: OrderSource.InStore,
      });
      setInStoreOrders(data.items);
      setListTotalPages(data.totalPages);
      setListTotalItems(data.totalItems);
    } finally {
      setIsLoadingOrders(false);
    }
  }, []);

  useEffect(() => {
    loadInStoreOrders(listPage);
  }, [listPage, loadInStoreOrders]);

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden bg-gray-50">
      <SmartNavbar />
      <div className="p-4 sm:p-14 max-w-full flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <PageTitle>BALCÃO</PageTitle>
          <Button
            className="flex items-center gap-2 px-4 py-2 text-sm"
            onClick={() => navigate(basePath + "/balcao/novo")}
          >
            <FaPlus /> Criar pedido
          </Button>
        </div>

        {isLoadingOrders ? (
          <div className="flex flex-col gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="animate-pulse bg-white border rounded p-4 shadow-sm space-y-2 h-32" />
            ))}
          </div>
        ) : inStoreOrders.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-500">
            <FaShoppingCart className="text-3xl" />
            <p className="text-sm sm:text-base">Nenhum pedido de balcão encontrado</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {inStoreOrders.map(order => (
              <OrderCard
                key={order.id}
                id={order.id}
                orderNumber={order.orderNumber}
                status={order.status}
                finalPrice={order.finalPrice}
                shippingMethod={order.shippingMethod}
                shippingDetails={order.shippingDetails}
                createdAt={order.createdAt}
                paymentMethod={order.paymentMethod}
                paymentReceivedAt={order.paymentReceivedAt}
                nfe={order.nfe}
                customerId={order.customerId}
                hasOrderProducts={order.hasOrderProducts}
                customerName={order.customerName ?? undefined}
                customerDocument={order.customerDocument ?? undefined}
                customerType={order.customerType}
                hasMadeToOrderItems={order.hasMadeToOrderItems}
                maxMadeToOrderLeadTimeDays={order.maxMadeToOrderLeadTimeDays}
                enablePrePostingLabel={false}
                showXmlDownload={tenantPreference?.enableNfeXmlDownload ?? true}
                orderSource={OrderSource.InStore}
                onCancel={async () => {
                  try {
                    await orderService.cancel({ id: order.id });
                    loadInStoreOrders(listPage);
                  } catch {
                    toast.error("Não foi possível cancelar o pedido.");
                  }
                }}
                onEmitNfe={async () => {
                  try {
                    await orderService.emitNfe(order.id);
                    loadInStoreOrders(listPage);
                  } catch {
                    toast.error("Não foi possível emitir a NF-e.");
                  }
                }}
                onCancelNfe={async (justification: string) => {
                  try {
                    await orderService.cancelNfe(order.id, justification);
                    loadInStoreOrders(listPage);
                  } catch {
                    toast.error("Não foi possível cancelar a NF-e.");
                  }
                }}
                onEditShippingDetailsModal={() => {}}
                onUpdateAvailability={() => {}}
              />
            ))}
          </div>
        )}

        {!isLoadingOrders && inStoreOrders.length > 0 && (
          <>
            <PaginationSummary
              totalItems={listTotalItems}
              singularLabel="pedido encontrado"
              pluralLabel="pedidos encontrados"
            />
            <Pagination
              currentPage={listPage}
              totalPages={listTotalPages}
              onPageChange={setListPage}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default AdminInStoreOrderPage;
